// -----------------------------------------------------------------------------
// Anaglyphic, Netherlands @ 2023.
// See README in the root project for more information.
// -----------------------------------------------------------------------------

import { defineStore } from "pinia";
import { Component } from "vue";
import { RouteRecordRaw } from "vue-router";
import { Toaster } from "../toaster";
import NavigationGroupDO = App.Data.NavigationGroupDO;
import NavigationDo = App.Data.NavigationItemDO;
import UserDO = App.Data.UserDO;
import EnvironmentDO = App.Data.EnvironmentDO;
import { ApiError } from "@/api/base";
import BexAdministrationDO = Modules.BookingExperts.Data.BexAdministrationDO;

// -----------------------------------------------------------------------------

export type AppMeta = {
	title: string;
	logoUrl: string;
	faviconUrl: string;
	navMenuUrl: string;
};

export interface BaseStore {
	toaster: Toaster | null;
	showInvalidPermissions: ApiError | null;
	isBackendUnreachable: boolean;
	overlay: {
		closeOnClickOutside?: boolean;
		component: Component;
		zIndex?: string;
		componentProps: object;
	} | null;
	userProvideEmail: string;
	currentUser: UserDO | null;
	currentEnvironment: EnvironmentDO | null;
	addMoreStoreValues: boolean;
	navigationMiniSidebar: boolean;
	navigationSidebarCollapsed: boolean;
	navigationShowMobileDrawer: boolean;
	navigationShowDesktopDrawer: boolean;
	showSecondaryDrawer: boolean;
	showBottomDrawer: boolean;
	secondaryDrawerWidth: number;
	secondaryDrawerComponent: Component | null;
	appTheme: "DARK_BLUE_THEME" | "BLUE_THEME";
	appFullWidth: boolean;
	isAuthenticated: boolean;
	permissions: string[];
	navigationStructure: NavigationGroupDO[] | null;
	moduleRoutesLoaded: boolean;
	loadingWrapper: boolean;
	loadingDefaultDelay: number;
	meta: AppMeta;
	selectedBexAdministration: BexAdministrationDO | null;
	isChatVisible: boolean;
	bexAdministrations: BexAdministrationDO[];
}

// -----------------------------------------------------------------------------

export const useBaseStore = defineStore({
	id: "baseStore",
	state: (): BaseStore => {
		return {
			toaster: null,
			showInvalidPermissions: null,
			isBackendUnreachable: false,
			overlay: null,
			currentUser: null,
			currentEnvironment: null,
			addMoreStoreValues: false,
			navigationMiniSidebar: true,
			navigationSidebarCollapsed: true,
			navigationShowDesktopDrawer: true,
			navigationShowMobileDrawer: false,
			showBottomDrawer: false,
			showSecondaryDrawer: false,
			secondaryDrawerWidth: 400,
			secondaryDrawerComponent: null,
			appTheme: "BLUE_THEME",
			appFullWidth: false,
			isAuthenticated: false,
			navigationStructure: null,
			permissions: [],
			moduleRoutesLoaded: false,
			loadingWrapper: false,
			loadingDefaultDelay: import.meta.env.VITE_DEFAULT_LOADING_DELAY
				? Number(import.meta.env.VITE_DEFAULT_LOADING_DELAY)
				: 250,
			meta: {
				title: "",
				navMenuUrl: "",
				faviconUrl: "",
				logoUrl: "",
			},
			selectedBexAdministration: null,
			isChatVisible: false,
			bexAdministrations: [],
			userProvideEmail: "",
		};
	},
	getters: {
		PRIMARY_NAVIGATION(state) {},
	},
	actions: {
		HAS_PERMISSION(permission: string) {
			return this.permissions.includes(permission);
		},
		HAS_ENVIRONMENT_FEATURE(feature: string) {
			return this.currentEnvironment.features.includes(feature);
		},
		SET_NAVIGATION_MINI_SIDEBAR(value: boolean) {
			this.navigationShowDesktopDrawer = true;
			this.navigationMiniSidebar = value;
		},
		SET_NAVIGATION_SHOW_MOBILE_DRAWER(value: boolean) {
			this.navigationShowDesktopDrawer = false;
			this.navigationShowMobileDrawer = value;
		},
		SET_APP_THEME(value: "DARK_BLUE_THEME" | "BLUE_THEME") {
			this.appTheme = value;
			localStorage.setItem("APP_THEME", value);
		},
		SET_NAVIGATION_VISIBILITY(navigationStructure: NavigationGroupDO[]): void {
			navigationStructure.forEach((navigationGroup: NavigationGroupDO) => {
				navigationGroup.navigation_items?.forEach(
					(navigationItem: NavigationDo) => {
						// no permissions set
						if (navigationItem.required_permissions?.length === 0) {
							navigationGroup.is_visible = true;
						} else {
							// permissions set
							navigationItem.required_permissions?.forEach(
								(permission: string) => {
									if (!this.permissions.includes(permission)) {
										navigationItem.is_visible = false;
									} else {
										navigationGroup.is_visible = true;
									}
								}
							);
						}
					}
				);
			});
			this.navigationStructure = navigationStructure;
		},
		SET_ACTIVE_BEX_ADMINISTRATION(administrationId: number): void {
			this.selectedBexAdministration = this.bexAdministrations.find(
				(administration) => administration.id === administrationId
			);
		},
	},
});
